import { Controller } from "@hotwired/stimulus"

// Stimulus controller for the tree view component.
//
// Handles:
// - Expand/collapse toggle with smooth animation
// - Persist expanded state in localStorage (keyed by tree ID + node ID)
//
// Usage (applied automatically by the tree_view helper / _tree_node partial):
//   <div class="app-tree-view" data-controller="tree" data-tree-tree-id-value="categories">
//     <div class="app-tree-node" data-tree-target="node" data-node-id="42">
//       <div class="app-tree-node__row">
//         <button data-action="tree#toggle" data-tree-target="toggle">...</button>
//       </div>
//       <div class="app-tree-node__children" data-tree-target="children">...</div>
//     </div>
//   </div>
//
export default class extends Controller {
  static targets = ["node", "toggle", "children"]

  static values = {
    treeId: { type: String, default: "tree" }
  }

  connect() {
    this._restoreState()
  }

  // Toggle a node's children container open/closed.
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const nodeEl = button.closest("[data-node-id]")
    if (!nodeEl) return

    const nodeId = nodeEl.dataset.nodeId
    const childrenEl = nodeEl.querySelector(":scope > .app-tree-node__children")
    if (!childrenEl) return

    const isExpanded = nodeEl.classList.contains("app-tree-node--expanded")

    if (isExpanded) {
      this._collapse(nodeEl, childrenEl, button)
      this._saveNodeState(nodeId, false)
    } else {
      this._expand(nodeEl, childrenEl, button)
      this._saveNodeState(nodeId, true)
    }
  }

  // --- Private helpers ---

  _expand(nodeEl, childrenEl, button) {
    nodeEl.classList.add("app-tree-node--expanded")
    childrenEl.style.maxHeight = childrenEl.scrollHeight + "px"
    childrenEl.style.opacity = "1"
    button.setAttribute("aria-expanded", "true")
    this._swapIcon(button, "fa-chevron-right", "fa-chevron-down")

    // After transition, remove max-height so dynamically loaded content can resize
    const handler = () => {
      childrenEl.style.maxHeight = "none"
      childrenEl.removeEventListener("transitionend", handler)
    }
    childrenEl.addEventListener("transitionend", handler)
  }

  _collapse(nodeEl, childrenEl, button) {
    // Set explicit height first so transition works
    childrenEl.style.maxHeight = childrenEl.scrollHeight + "px"
    // Force reflow
    childrenEl.offsetHeight // eslint-disable-line no-unused-expressions
    childrenEl.style.maxHeight = "0px"
    childrenEl.style.opacity = "0"

    nodeEl.classList.remove("app-tree-node--expanded")
    button.setAttribute("aria-expanded", "false")
    this._swapIcon(button, "fa-chevron-down", "fa-chevron-right")
  }

  _swapIcon(button, from, to) {
    const iconEl = button.querySelector("i")
    if (iconEl) {
      iconEl.classList.remove(from)
      iconEl.classList.add(to)
    }
  }

  // --- localStorage persistence ---

  _storageKey() {
    return `tree-expanded-${this.treeIdValue}`
  }

  _getExpandedSet() {
    try {
      const raw = localStorage.getItem(this._storageKey())
      return raw ? new Set(JSON.parse(raw)) : new Set()
    } catch {
      return new Set()
    }
  }

  _saveNodeState(nodeId, expanded) {
    const set = this._getExpandedSet()
    if (expanded) {
      set.add(String(nodeId))
    } else {
      set.delete(String(nodeId))
    }
    try {
      localStorage.setItem(this._storageKey(), JSON.stringify([...set]))
    } catch {
      // Storage full or unavailable — silently ignore
    }
  }

  _restoreState() {
    const expandedSet = this._getExpandedSet()
    if (expandedSet.size === 0) return

    const nodes = this.element.querySelectorAll("[data-node-id]")
    nodes.forEach((nodeEl) => {
      const nodeId = nodeEl.dataset.nodeId
      if (!expandedSet.has(String(nodeId))) return

      const childrenEl = nodeEl.querySelector(":scope > .app-tree-node__children")
      const button = nodeEl.querySelector(":scope > .app-tree-node__row [data-action*='tree#toggle']")
      if (!childrenEl || !button) return

      // Expand without animation on restore
      nodeEl.classList.add("app-tree-node--expanded")
      childrenEl.style.maxHeight = "none"
      childrenEl.style.opacity = "1"
      button.setAttribute("aria-expanded", "true")
      this._swapIcon(button, "fa-chevron-right", "fa-chevron-down")
    })
  }
}
